import { Banner } from 'slab'
import { getFormattedDate } from 'utils'
import {
  type MemberOnboardingMemberPlanChangesProps,
  type MemberOnboardingRenewalDentalPlan,
  type MemberOnboardingRestrictions,
} from 'utils/web_api.schemas'

import {
  isCurrentDentalPlanOffThatch,
  isMaxCoverageOverThreeYears,
} from '../../utils'
import { PartialCoverageWarningBanner } from '../PartialCoverageWarningBanner/PartialCoverageWarningBanner'
import RenewalsPlanComparisonHeader from '../RenewalsPlanComparisonHeader/RenewalsPlanComparisonHeader'
import RenewalsPlanComparisonTable, {
  type RowValues,
} from '../RenewalsPlanComparisonTable/RenewalsPlanComparisonTable'

export interface DentalPlanChangesTableProps {
  planChangeSummary: MemberOnboardingMemberPlanChangesProps;
  hasFamily: boolean;
  restrictions: MemberOnboardingRestrictions;
}

export const DentalPlanChangesTable = ({
  planChangeSummary,
  hasFamily,
  restrictions,
}: DentalPlanChangesTableProps) => {
  const currentYear = planChangeSummary.current_plan_year
  const nextYear = planChangeSummary.next_plan_year
  const yearOverYearChange = planChangeSummary.year_over_year_change

  const currentYearDentalPlan = currentYear.dental_plan
  const nextYearDentalPlan = nextYear.dental_plan

  // Early return if no dental plans
  if (!currentYearDentalPlan) {
    return null
  }

  let annualMaxCoverageLabel = 'Annual max coverage'
  if (isMaxCoverageOverThreeYears(nextYearDentalPlan?.annual_max_coverage)) {
    annualMaxCoverageLabel = 'Max coverage over 3 years'
  }

  const dentalPlanRowValues: RowValues[] = [
    {
      label: 'Monthly premium',
      prevValue: currentYearDentalPlan.premium_amount.formatted_amount,
      newValue:
        nextYearDentalPlan?.premium_amount.formatted_amount || 'Unknown',
      yoyChange: nextYearDentalPlan
        ? yearOverYearChange.dental_amount
        : undefined,
      increaseIsGreen: false,
    },
  ]

  const onThatchDentalPlanRowValues: RowValues[] = [
    {
      // For BSCA dental plans, we show family deductible if the user has a family
      label:
        hasFamily && nextYearDentalPlan?.category === 'bsca_dental'
          ? 'Family deductible'
          : 'Deductible',
      prevValue: currentYearDentalPlan.deductible,
      newValue: nextYearDentalPlan?.deductible || 'Unknown',
      // We only show YOY change for BSCA dental plans because they are going to be a numeric value for sure
      // For other values we cannot guarantee that the value is a numeric value
      yoyChange:
        nextYearDentalPlan && nextYearDentalPlan?.category === 'bsca_dental'
          ? yearOverYearChange.dental_feature_change.deductible
          : undefined,
      increaseIsGreen: false,
    },
    {
      label: annualMaxCoverageLabel,
      prevValue: currentYearDentalPlan?.annual_max_coverage,
      newValue: nextYearDentalPlan?.annual_max_coverage || 'Unknown',
      // We only show YOY change for BSCA dental plans because they are going to be a numeric value for sure
      // For other values we cannot guarantee that the value is a numeric value
      yoyChange:
        nextYearDentalPlan && nextYearDentalPlan?.category === 'bsca_dental'
          ? yearOverYearChange.dental_feature_change.annual_max_coverage
          : undefined,
      increaseIsGreen: false,
    },
  ]

  const offThatchDental = isCurrentDentalPlanOffThatch(planChangeSummary)
  const rows = offThatchDental
    ? dentalPlanRowValues
    : [...dentalPlanRowValues, ...onThatchDentalPlanRowValues]

  const planCannotBeChanged =
    restrictions?.dental_not_available === 'coverage_ends_mid_year'

  let thereIsNoNextPlanMessage
  if (planCannotBeChanged) {
    thereIsNoNextPlanMessage = 'Your plan will stay the same.'
  } else if (
    !nextYearDentalPlan &&
    currentYearDentalPlan?.category !== 'off_thatch_dental'
  ) {
    thereIsNoNextPlanMessage = `Your ${currentYear.plan_year} plan isn't available for ${nextYear.plan_year}.`
  }

  let plansToUseForHeader:
    | MemberOnboardingRenewalDentalPlan
    | null
    | undefined = nextYearDentalPlan
  if (planCannotBeChanged) {
    plansToUseForHeader = currentYearDentalPlan
  } else if (
    !nextYearDentalPlan &&
    currentYearDentalPlan?.category === 'off_thatch_dental'
  ) {
    plansToUseForHeader = null
  }

  return (
    <div className='flex flex-col gap-6 border border-gray-200 rounded-2xl shadow-sm p-4 xs:p-6'>
      <RenewalsPlanComparisonHeader
        plan={plansToUseForHeader}
        planType='dental'
      />
      {offThatchDental && (
        <Banner
          title='You purchased this plan outside of Thatch.'
          type='notice'
        />
      )}
      {planCannotBeChanged && (
        <Banner
          title={`Your current plan started ${getFormattedDate(
            currentYearDentalPlan?.coverage_start_date
          )}. You won't be able to select a new plan for ${
            nextYear.plan_year
          } coverage and your premium and plans features will remain the same as ${
            currentYear.plan_year
          }. `}
          type='notice'
        />
      )}
      <PartialCoverageWarningBanner planChangeSummary={planChangeSummary} coverageType='dental' />
      <RenewalsPlanComparisonTable
        prevPlanYear={currentYear.plan_year}
        currPlanYear={nextYear.plan_year}
        rows={rows}
        onNavigateToPrevPlanDetailsPage={
          !offThatchDental
            ? () => {
                window.open(currentYearDentalPlan.sbc_url || '', '_blank')
              }
            : undefined
        }
        onNavigateToCurrPlanDetailsPage={
          !planCannotBeChanged && nextYearDentalPlan
            ? () => {
                window.open(nextYearDentalPlan?.sbc_url || '', '_blank')
              }
            : undefined
        }
        thereIsNoNextPlanMessage={thereIsNoNextPlanMessage}
      />
    </div>
  )
}
